/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ProductVariants } from "@farmlands/commerce";
import { colors } from "@farmlands/mui-theme";
import { Divider, SelectChangeEvent, styled, useMediaQuery, useTheme } from "@mui/material";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import DropdownList, { DropdownItem } from "../../../../components/Dropdownlist";
import ToastIcon from "../../../../components/ToastIcon";
import { TestIds } from "../../../../testIds";
import { CartItem } from "../../../cart/storage/cart.types";
import {
  getVariantDimensionByType,
  getVariantDimensionByValue,
} from "../../../cart/utils/get-variant-dimension";
import { Dimension, Product, Variant } from "../../storage";
import { getAvailableColours } from "../../utils/getAvailableColours";
import useVariants, { DimensionTypes, knownDimensionNames } from "../../utils/useVariations";

export const VariantsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  columnGap: "24px",
  rowGap: "8px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

export interface ProductVariantDetailsProps {
  item: Product | CartItem;
  enableVariants?: boolean;
  totalOrderedQty?: number;
  onChangeVariant?: (variant: Variant) => void;
}

export const ProductVariantDetails = ({
  item: { masterProductId, productId, ...item },
  enableVariants,
  onChangeVariant,
  totalOrderedQty,
}: ProductVariantDetailsProps) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { variant, variants, dimensions, error, isLoading, defaultDimension } = useVariants(
    productId,
    masterProductId,
    !!onChangeVariant
  );

  const allColours = dimensions
    ? dimensions[1]?.map((e) => e.value)
    : [item.colourDescription ?? item.colour ?? ""];
  const [selectedSize, setSelectedSize] = useState<string | undefined>(item.sizeDescription);
  const [selectedColour, setSelectedColour] = useState<string | undefined>(item.colourDescription);

  // Always update cart item, particularly for EditPriceModal
  useEffect(() => {
    if ((item as CartItem)?.lineId) {
      setSelectedSize(item.sizeDescription);
      setSelectedColour(item.colourDescription);
    }
  }, [item]);

  useEffect(() => {
    if (!selectedSize && defaultDimension) {
      if (defaultDimension.colour !== undefined) {
        setSelectedColour(defaultDimension.colour);
      }
      if (defaultDimension.size !== undefined) {
        setSelectedSize(defaultDimension.size);
      }
      if (variant && onChangeVariant) {
        onChangeVariant(variant);
      }
    }
  }, [defaultDimension, selectedSize, variant, onChangeVariant]);

  const availableColours = useMemo(() => {
    if (selectedSize && selectedColour) {
      const colours = getAvailableColours(variants, selectedSize);
      const newArr: DropdownItem[] = allColours?.map((x) => {
        if (colours.find((y) => y.value === x)) {
          return { value: x, disabled: false };
        }
        return { value: `${x} (Unavailable)`, disabled: true };
      });
      return newArr;
    }
    if (!selectedSize && selectedColour) {
      return allColours.map((x) => {
        return { value: x, disabled: false };
      });
    }
    return [];
  }, [variants, selectedSize, allColours]);

  const handleDimensionChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const currentVariant = variants?.variants.find((v) => v.productId === productId);
    let dimension: Dimension | undefined;
    variants?.variants.find((variant) => {
      dimension = getVariantDimensionByValue(variant, value as string) ?? dimension;
    });

    let colours: Dimension[] | undefined = [];
    let isForceUpdateColour = false;

    if (dimension?.type === DimensionTypes.SIZE) {
      setSelectedSize(dimension.value);

      if (selectedColour) {
        // check current colour in available colours
        colours = getAvailableColours(variants, dimension?.value);
        if (!colours?.find((x) => x.value === selectedColour)) {
          isForceUpdateColour = true;
          toast(`${selectedColour} is unavailable`, {
            type: "error",
            autoClose: false,
            icon: <ToastIcon>error</ToastIcon>,
          });
        }
      }
    }

    if (dimension?.type === DimensionTypes.COLOUR) {
      setSelectedColour(dimension.value);
    }

    const dmap = new Map<number, Dimension>();

    currentVariant?.dimensions.forEach((dim) => dmap.set(dim.type, dim));
    if (dimension) {
      dmap.set(dimension?.type, dimension);
    }

    // if selected colour is not available with the size, set colour as the first available options
    if (isForceUpdateColour && colours) {
      dmap.set(1, colours[0]);
      setSelectedColour(colours[0].value);
    }

    const values = Array.from(dmap.values());
    const newVariant = variants?.variants.find((variant) => {
      return _.isEqual(variant?.dimensions, values);
    });

    if (newVariant && onChangeVariant) {
      onChangeVariant(newVariant);
    }
  };

  const ColourDropdown = React.useMemo(() => {
    if (enableVariants && selectedColour) {
      return (
        <DropdownList
          testId={TestIds.feat.product.variant.colourDropdown}
          sxFormControl={isSmall ? {} : { flexShrink: 1, maxWidth: "164px" }}
          sx={isSmall ? {} : { maxWidth: "164px" }}
          label={knownDimensionNames[1] ?? ""}
          items={availableColours}
          onChange={handleDimensionChange}
          value={
            getVariantDimensionByType(variant, DimensionTypes.COLOUR)?.value ??
            item.colourDescription ??
            item.colour
          }
        />
      );
    }
  }, [
    knownDimensionNames,
    dimensions,
    variants,
    variant,
    item.colour,
    enableVariants,
    selectedColour,
    error,
    isLoading,
    isSmall,
    availableColours,
  ]);

  const SizeDropdown = React.useMemo(() => {
    const allSizes = dimensions
      ? dimensions[3]?.map((e) => e.value)
      : [item.sizeDescription ?? item.size ?? ""];

    if (enableVariants && selectedSize) {
      return (
        <DropdownList
          testId={TestIds.feat.product.variant.sizeDropdown}
          sxFormControl={isSmall ? {} : { flexShrink: 1, maxWidth: "164px" }}
          sx={isSmall ? {} : { maxWidth: "164px" }}
          label={knownDimensionNames[3] ?? ""}
          items={allSizes?.map((x) => {
            return { value: x, disabled: false };
          })}
          onChange={handleDimensionChange}
          value={
            getVariantDimensionByType(variant, DimensionTypes.SIZE)?.value ??
            item.sizeDescription ??
            item.size
          }
        />
      );
    }
  }, [
    knownDimensionNames,
    dimensions,
    defaultDimension,
    selectedSize,
    variants,
    variant,
    item.colour,
    enableVariants,
    error,
    isLoading,
    isSmall,
  ]);

  if (!enableVariants) {
    return (
      <ProductVariants
        size={selectedSize ?? item.size}
        colour={selectedColour ?? item.colour}
        totalOrdered={totalOrderedQty}
      />
    );
  }

  return (
    <VariantsContainer data-testid={TestIds.feat.product.variant.container}>
      {(item.colour || item.size) && (
        <Divider sx={{ width: "100%", borderColor: colors.primary100 }} />
      )}
      {SizeDropdown}
      {ColourDropdown}
    </VariantsContainer>
  );
};
